// The treasure on the peek and the card (v2-the-deep.md §9.1, §9.2, §15.5,
// round 088 card 10, R2629, R2643, R2649).
//
// The point menu of round 088 card 10 is not built (R2629): no new gesture
// and no timer. The pinned peek of water and the peek of a wreck gain
// SEARCH HERE while an own fleet is selected and the flag `treasure`
// holds; on a wreck with cargo the fleet searches at the wreck point, and
// the SEARCH recovers its cargo (R2860). With no own fleet selected, the
// peek gains SEARCH, which opens the SEARCH picker of the cache sheet
// (R2699) for the cell, or for the wreck point on a wreck with cargo. The
// press stands beside MARK, and a right click stays MOVE. The hub judges
// the order, and a refusal arrives as a notice.
//
// The fleet card states the work of a SEARCH: the time left at
// the point, counted down between two frames, or the word of a fleet that
// still sails there. It states the search hint as the echo line (chart
// card 33, round 088 card 10): a small echo-sounder trace whose one spike
// grows from the far to the near third of the reach, and a word. The spike
// stands in the middle of the trace at every hint, so the line names no
// bearing, as the hint names no position. The wreck card states the cargo
// sighting of the commander (R2843): the take of a SEARCH and the age of
// the sighting. The public wreck names no cargo, so a wreck with no
// sighting is a plain wreck. Both draw what the wire sends: the server
// sends the fields only while `treasure` is latched.
//
// Everything here is pure, so a test reads it with no overlay.

import { type CargoSighting, Mission, type Resources, type Wreck } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { type GameState, costText, dur, num } from '../../state';
import { mpxToPx } from '../../world';
import { esc } from '../esc';
import type { Action, Fact } from './card';

/** The act of CACHES in the ⋯ of an own fleet card (the fleet id): it
 *  opens the cache sheet of `ui/caches.ts` (R2649). */
export const CACHE_OPEN = 'cacheopen';

/** CACHES in the ⋯ of an own fleet card while `treasure` holds, or null. */
export function cachesItem(s: Pick<GameState, 'hasFeatureFlag'>, fleetId: number): Action | null {
  if (!s.hasFeatureFlag('treasure')) return null;
  return { act: CACHE_OPEN, id: fleetId, face: 'quiet', label: S.o_caches, title: S.o_caches_title };
}

/** The act of SEARCH on the card of a selected point of interest
 *  (R3138): `n` is the cell of the point. It opens the SEARCH picker of
 *  the cell (R2699). */
export const POI_SEARCH = 'poisearch';

/**
 * SEARCH on the card of a selected point of interest (R3138), or null.
 * The press is the work press of the peek with no own fleet selected
 * (R2699): a selected point holds the selection, so the press opens the
 * SEARCH picker of the cell of the point. The flag `treasure` must hold;
 * with it off, the card carries no press, as the peek carries none.
 */
export function pointSearch(s: Pick<GameState, 'hasFeatureFlag'>, cell: number): Action | null {
  if (!s.hasFeatureFlag('treasure') || cell < 0) return null;
  return { act: POI_SEARCH, n: cell, face: 'dark', label: S.search_pick, title: S.search_pick_title };
}

/** A press of the peek: its word and what a press does (`PeekAction`). */
export interface WorkPress {
  label: string;
  title: string;
  run(): void;
}

/** Is the mission the work order SEARCH (R2860)? */
function isWorkMission(m: Mission): boolean {
  return m === Mission.MISSION_SEARCH;
}

/** What a reader of the cargo sightings reads of the state. */
export type SightingState = Pick<GameState, 'cargoSightings'>;

/** The cargo sighting of a wreck (v2 §9.6, R2843), or undefined: the
 *  public wreck names no cargo, so a wreck the sonar of this commander
 *  never heard with cargo reads as a plain wreck. A row can be the
 *  recovered mark (R2850); `wreckTakes` reads it as no cargo. */
export function sightingOf(s: SightingState, fleet: number): CargoSighting | undefined {
  return s.cargoSightings.get(fleet);
}

/** Can a SEARCH take cargo from this wreck, as far as this commander
 *  knows? Only a wreck with a cargo sighting that is not recovered can
 *  (R2850). The sighting can be stale: a SEARCH takes nothing from a wreck
 *  that is recovered or empty (R2860). The wire carries sightings only while `treasure`
 *  is latched. */
export function wreckTakes(s: SightingState, w: Pick<Wreck, 'fleet'>): boolean {
  const r = s.cargoSightings.get(w.fleet);
  return r !== undefined && !r.recovered;
}

/** What the work press of the peek reads beside the state. */
export interface WorkPressInput {
  /** The selected own fleet, or 0 for none. */
  fleetId: number;
  /** The selected fleet takes a SEARCH: a combat ship or a
   *  Dredger (`workFit`, v2 §9.6, R2834). An unfit selection reads as
   *  none, so the press opens the picker of the fit hulls. */
  fit: boolean;
  /** The cell of the press, −1 off the map. */
  cell: number;
  /** The cell is rock: no hull sails there, so no SEARCH. */
  rock: boolean;
  /** The wreck of the wreck window on the cell, or null. */
  wreck: Wreck | null;
  /** SEARCH HERE: the fleet sails to the cell and searches it. */
  search(fleetId: number, cell: number): void;
  /** SEARCH HERE on a wreck (R2860): the fleet sails to the wreck point
   *  (x, y) world px and searches there. */
  searchPoint(fleetId: number, x: number, y: number): void;
  /** SEARCH with no selected own fleet (R2699): opens the SEARCH picker
   *  of the cache sheet for the cell, at the point (x, y) of a wreck with
   *  cargo when one is given (R2860). A page that wires nothing offers no
   *  press without a selection. */
  pick?(cell: number, at?: readonly [number, number]): void;
}

/**
 * The work press of a pinned peek (R2629, R2649, R2699, R2860), or null.
 * With a selected own fleet, a wreck with cargo gives SEARCH HERE at the
 * wreck point and any other water gives SEARCH HERE at the cell. With no
 * selected own fleet, a wreck with cargo gives SEARCH, which opens the
 * SEARCH picker at the wreck point, and water that is not rock gives
 * SEARCH, which opens the SEARCH picker of the cell:
 * a click of a mouse on the water drops the selection before the peek
 * stands, so the peek of a desktop never holds one (R2699). The flag must
 * hold; with it off, the peek keeps MARK alone, as it stood before phase 5.
 */
export function workPress(s: Pick<GameState, 'hasFeatureFlag'> & SightingState, i: WorkPressInput): WorkPress | null {
  if (!s.hasFeatureFlag('treasure') || i.cell < 0) return null;
  const w = i.wreck && wreckTakes(s, i.wreck) ? i.wreck : null;
  const point: readonly [number, number] | null = w ? [mpxToPx(w.xMpx), mpxToPx(w.yMpx)] : null;
  if (i.fleetId === 0 || !i.fit) {
    const pick = i.pick;
    if (!pick || (i.rock && !point)) return null;
    const at = i.cell;
    if (point) return { label: S.search_pick, title: S.search_wreck_pick_title, run: () => pick(at, point) };
    return { label: S.search_pick, title: S.search_pick_title, run: () => pick(at) };
  }
  const fleet = i.fleetId;
  if (point) {
    return { label: S.search_here, title: S.search_wreck_title, run: () => i.searchPoint(fleet, point[0], point[1]) };
  }
  if (i.rock) return null;
  const cell = i.cell;
  return { label: S.search_here, title: S.search_here_title, run: () => i.search(fleet, cell) };
}

/** The work fact of an own fleet on a SEARCH (R2649): the
 *  time left at the point in world time, or the word of a fleet that sails
 *  there. Null for every other mission. */
export function workFact(s: Pick<GameState, 'mapToWorldSec'>, f: { mission: Mission; workLeftSec?: number }): Fact | null {
  if (!isWorkMission(f.mission)) return null;
  const left = f.workLeftSec ?? 0;
  return {
    key: S.f_work,
    val: esc(left > 0 ? S.work_left({ left: dur(Math.ceil(s.mapToWorldSec(left))) }) : S.work_sailing),
  };
}

/** The compressed cargo of an own hull in the Empire frame (v2 §9.6,
 *  R2834), or null while it carries none. */
function compressedOf(s: Pick<GameState, 'empire'>, fleetId: number): Resources | null {
  return s.empire?.compressed.find((c) => c.fleetId === fleetId)?.cargo ?? null;
}

/** The units of a bundle of cargo, every resource of it: compressed
 *  cargo, and the take of a cargo sighting (the cell list, R2851). */
export function cargoUnits(r: Resources): number {
  return r.alloy + r.biomass + r.volatiles + r.isotopes + r.credits;
}

/** The compressed fact of an own fleet (v2 §9.6, R2834): the units that a
 *  SEARCH took aboard and that land at the own hold, with
 *  each resource in the title. Null while the hull carries none. */
export function compressedFact(s: Pick<GameState, 'empire'>, fleetId: number): Fact | null {
  const c = compressedOf(s, fleetId);
  if (!c || cargoUnits(c) === 0) return null;
  return {
    key: S.f_compressed,
    val: esc(S.compressed_cargo({ units: num(cargoUnits(c)) })),
    title: S.compressed_title({
      alloy: num(c.alloy), biomass: num(c.biomass), volatiles: num(c.volatiles), isotopes: num(c.isotopes),
    }),
  };
}

/** The cargo fact of a wreck (R2649, R2843): the take of a SEARCH with
 *  the resource icons of a build price, and the age of the sighting on
 *  the world clock. A recovered row states "recovered" and the age of the
 *  moment this commander learned it (R2850). Null for a wreck with no
 *  cargo sighting. */
export function wreckCargoFact(
  s: SightingState & Pick<GameState, 'seasonElapsedSec'>, w: Pick<Wreck, 'fleet'>,
): Fact | null {
  const r = sightingOf(s, w.fleet);
  if (!r) return null;
  if (r.recovered) return { key: S.f_cargo, val: esc(`${S.wreck_recovered} · ${seenText(s, r)}`) };
  return {
    key: S.f_cargo,
    val: `${costText(r.take)} · ${esc(seenText(s, r))}`,
    title: S.wreck_cargo,
  };
}

/** The age of a cargo sighting on the world clock: "seen 2 h ago". */
export function seenText(s: Pick<GameState, 'seasonElapsedSec'>, r: Pick<CargoSighting, 'seenSec'>): string {
  return S.wreck_seen({ ago: dur(Math.max(0, s.seasonElapsedSec() - r.seenSec)) });
}

/** The height of the spike of the echo trace for each hint, in the units
 *  of its 56 × 14 box: the far, the middle and the near third. */
const ECHO_SPIKE = [0, 4, 8, 12];

/**
 * The echo trace of a hint (chart card 33): a flat return with one spike in
 * the middle. The trace is a fixed shape, so two hulls with one hint draw
 * one line and the line names no bearing.
 */
export function echoTraceSvg(hint: number): string {
  const h = ECHO_SPIKE[Math.max(0, Math.min(3, hint))];
  const base = 12;
  const pts: string[] = [];
  for (let i = 0; i <= 28; i++) {
    const x = i * 2;
    const ripple = i % 2 === 0 ? 0 : 0.8;
    const y = i === 14 ? base - h : i === 13 || i === 15 ? base - h / 2 : base - ripple;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return '<svg class="ck-echo" viewBox="0 0 56 14" width="56" height="14" aria-hidden="true" focusable="false">'
    + `<polyline points="${pts.join(' ')}" fill="none" stroke="currentColor" stroke-width="1.2"`
    + ' stroke-linejoin="round"/></svg>';
}

/** The word of a hint: a faint, a clear or a hard return. */
function echoWord(hint: number): string {
  if (hint >= 3) return S.echo_near;
  return hint === 2 ? S.echo_mid : S.echo_far;
}

/**
 * The echo fact of an own fleet (v2 §9.1, R2643, R2649): the trace and the
 * word of its search hint, or null for no hint. It draws what the wire
 * sends, and the wire sends a hint only while `treasure` is latched.
 */
export function echoFact(s: Pick<GameState, 'rules'>, f: { searchHint?: number }): Fact | null {
  const hint = f.searchHint ?? 0;
  if (hint <= 0) return null;
  return {
    key: S.f_echo,
    val: `${echoTraceSvg(hint)}${esc(echoWord(hint))}`,
    title: S.echo_title({ px: Math.round(s.rules.cacheHintPx) }),
  };
}
